'use client'

import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { keyBy } from 'lodash'
import { InView } from 'react-intersection-observer'
import { v4 as uuid } from 'uuid'
import { Badge, Button, Cell, Divider, Icon, Image, Navigation } from '@vinted/web-ui'
import { ArrowLeft24, Dots24 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import {
  getCatalogPhotoSrc,
  CatalogNavigationModel,
  CatalogItem,
  CatalogItemData,
  CatalogAttribute,
  ROOT_CATALOG_ID,
  WEB_CATALOG_ROOT_ALL_CODE,
  Filter,
  SELECT_LARGER_DROPDOWN_MIN_WIDTH,
  SELECT_LARGER_DROPDOWN_MAX_HEIGHT,
} from '@marketplace-web/domain/catalog-data'
import { useCatalogContext } from '@marketplace-web/domain/catalog-page-data'

import { clickFilterEvent, selectCatalogEvent } from '_libs/common/event-tracker/events'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/domain/catalog-filters-data'
import { Screen } from 'constants/tracking/screens'

import SelectableItemList, { RenderItemProps } from '../../common/SelectableItemList'
import useCatalogFilter from '../../../../hooks/useCatalogFilter'
import useFetchDynamicFilters from '../../../../hooks/useFetchDynamicFilters'
import useNavStack from '../../../../hooks/useNavStack'
import FilterDropdown from '../../common/FilterDropdown'

const CatalogFilter = () => {
  const translate = useTranslate('catalog.filters')
  const fetchDynamicFilters = useFetchDynamicFilters()
  const { track } = useTracking()
  const {
    state: { data, filters, dynamicFilters, searchSession },
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchSession
  const searchSessionId = searchSession.sessionId
  const { selectedDynamicFilters } = dynamicFilters

  const selectedIds = filters.catalogIds
  const catalogs = useMemo(() => keyBy(data.catalogs, 'id'), [data.catalogs])

  const [navigatedCatalogId, setNavigatedCatalogId] = useState<number>(ROOT_CATALOG_ID)
  const [openedCatalogsIds, setOpenedCatalogsIds] = useState<Array<number>>([ROOT_CATALOG_ID])
  const [isCatalogForceClose, setIsCatalogForceClose] = useState<boolean>(false)
  const { allShownCatalogs } = useCatalogFilter(
    openedCatalogsIds,
    translate('catalog.all'),
    catalogs,
  )
  const [navStack] = useNavStack(data.catalogTree, selectedIds)

  const seenItems = useRef(new Set<number>())
  const filteringSessionId = useRef(uuid())

  useEffect(() => {
    seenItems.current.clear()
  }, [openedCatalogsIds])

  // on mount or when selectedIds change, navigate to the selected catalog's level
  useEffect(() => {
    const targetCatalogId: number = selectedIds?.[0] ?? ROOT_CATALOG_ID
    const targetCatalog = catalogs[targetCatalogId]
    const isValidNavStack: boolean =
      targetCatalog !== undefined && navStack.at(-1) === targetCatalogId

    let finalNavigatedId: number = ROOT_CATALOG_ID
    let newOpenedCatalogsIds: Array<number> = [ROOT_CATALOG_ID]

    // navigate to parent or target
    if (targetCatalog && isValidNavStack) {
      if (targetCatalog.catalogIds.length === 0) {
        finalNavigatedId = targetCatalog.parentId ?? ROOT_CATALOG_ID
        newOpenedCatalogsIds = navStack.slice(0, -1).filter(id => catalogs[id]) || [ROOT_CATALOG_ID]
      } else {
        finalNavigatedId = targetCatalogId
        newOpenedCatalogsIds = navStack.filter(id => catalogs[id])
      }
    } else if (targetCatalog) {
      if (targetCatalog.catalogIds.length === 0) {
        finalNavigatedId = targetCatalog.parentId ?? ROOT_CATALOG_ID
        newOpenedCatalogsIds = [finalNavigatedId]
      } else {
        finalNavigatedId = targetCatalogId
        newOpenedCatalogsIds = [targetCatalogId]
      }
    }

    setNavigatedCatalogId(finalNavigatedId)
    setOpenedCatalogsIds(newOpenedCatalogsIds)
  }, [navStack, selectedIds, catalogs])

  const handleOnClose = () => {
    seenItems.current.clear()
    filteringSessionId.current = uuid()
    setIsCatalogForceClose(false)
  }

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Category,
        screen: Screen.CatalogFilters,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const trackCatalogSelection = (catalogIds: Array<number>) => {
    track(
      selectCatalogEvent({
        catalogIds,
        attributeId: CatalogAttribute.Filter,
      }),
    )
  }

  const trackCatalogClick = (catalog: CatalogItemData, index: number) => {
    const isSelected = !selectedIds.includes(catalog.id)

    track(
      userClickDynamicFilterOptionEvent({
        id: catalog.id,
        isSelected,
        code: 'catalog',
        type: 'catalog',
        fromScreen: Screen.Catalog,
        position: index + 1,
        filteringSessionId: filteringSessionId.current,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const handleCatalogClick = (catalog: CatalogItem, _: MouseEvent, index: number) => {
    if (!catalog.data) return

    const { catalogIds, code } = catalog.data
    const catalogId = Number(catalog.id)

    trackCatalogClick(catalog.data, index)

    if (catalogIds.length > 0 && code !== WEB_CATALOG_ROOT_ALL_CODE) {
      setNavigatedCatalogId(catalogId)
      setOpenedCatalogsIds(prev => [...prev, catalogId])

      return
    }

    const newSelectedIds = selectedIds.includes(catalogId)
      ? selectedIds.filter(id => id !== catalogId)
      : [catalogId]

    trackCatalogSelection(newSelectedIds)
    fetchDynamicFilters({ selectedDynamicFilters, selectedFilters: { catalogIds: newSelectedIds } })

    setIsCatalogForceClose(true)
  }

  const handleBackClick = () => {
    const parentStack = openedCatalogsIds.slice(0, -1)
    const parentId: number =
      parentStack.length > 0
        ? parentStack[parentStack.length - 1] ?? ROOT_CATALOG_ID
        : ROOT_CATALOG_ID
    setNavigatedCatalogId(parentId)
    setOpenedCatalogsIds(parentStack.length > 0 ? parentStack : [ROOT_CATALOG_ID])
  }

  const renderNavigation = () => {
    if (navigatedCatalogId === ROOT_CATALOG_ID) return null

    const catalog = catalogs[navigatedCatalogId]

    if (!catalog) return null

    return (
      <>
        <Navigation
          left={
            <Button
              aria={{ 'aria-label': translate('a11y.actions.back') }}
              styling={Button.Styling.Flat}
              theme="amplified"
              icon={<Icon name={ArrowLeft24} />}
              onClick={handleBackClick}
              testId="catalog-filter-go-back"
            />
          }
          body={catalog.title}
        />
        <Divider />
      </>
    )
  }

  const renderIcon = (photo: CatalogNavigationModel['photo'], code: string, depth: number) => {
    if (depth > 1) return null

    if (code === WEB_CATALOG_ROOT_ALL_CODE) {
      return <Icon name={Dots24} color={Icon.Color.Primary} />
    }

    if (!photo) return null

    return <Image {...getCatalogPhotoSrc(photo)} size={Image.Size.Regular} />
  }

  const handleInView = (catalog: CatalogItemData, index: number) => {
    if (seenItems.current.has(catalog.id)) return
    seenItems.current.add(catalog.id)

    track(
      userViewDynamicFilterOptionEvent({
        id: catalog.id,
        code: 'catalog',
        type: 'catalog',
        fromScreen: Screen.Catalog,
        position: index + 1,
        filteringSessionId: filteringSessionId.current,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const renderCatalog = (
    { item, itemElementProps: { key, ...itemElementProps } }: RenderItemProps<CatalogItemData>,
    index: number,
  ) => {
    if (!item.data) return null

    const { catalogIds, code, depth, photo, badge } = item.data
    const icon = renderIcon(photo, code, depth)

    if (!catalogIds.length || code === WEB_CATALOG_ROOT_ALL_CODE) {
      return <Cell key={key} {...itemElementProps} prefix={icon} />
    }

    return (
      <InView
        key={item.value}
        onChange={inView => inView && item.data && handleInView(item.data, index)}
      >
        <Cell
          key={key}
          {...itemElementProps}
          prefix={icon}
          suffix={badge && <Badge theme={badge.theme || 'primary'} content={badge.title} />}
          chevron
        />
      </InView>
    )
  }

  const renderDropdown = () => {
    const shownCatalogs = allShownCatalogs[navigatedCatalogId] || []

    return (
      <div className="u-ui-padding-vertical-x-small">
        {renderNavigation()}

        <ScrollableArea maxHeight={SELECT_LARGER_DROPDOWN_MAX_HEIGHT}>
          <SelectableItemList
            name="catalog_ids"
            isMultiSelect={false}
            items={shownCatalogs}
            selected={selectedIds}
            onItemClick={handleCatalogClick}
            renderItem={renderCatalog}
            disableButtonClicks
          />
        </ScrollableArea>
      </div>
    )
  }

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        title={translate('catalog.name')}
        minWidth={SELECT_LARGER_DROPDOWN_MIN_WIDTH}
        areAnyOfItemsSelected={!!selectedIds.length}
        testId="catalog--catalog-filter"
        useChipAsTrigger
        useForceClose={isCatalogForceClose}
        onFilterClick={handleOnFilterClick}
        onClose={handleOnClose}
      >
        {renderDropdown()}
      </FilterDropdown>
    </div>
  )
}

export default CatalogFilter
